import React, { useCallback } from 'react';
import { Button } from '@vibe/core';
import type { BoardResourcesReportResponse } from '../../api/report.api';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleDataRow, styleTotalRow,
  setColumnWidths, downloadWorkbook, round, hexToArgb, COLORS,
} from '../../utils/export-report-excel';

// ─── Compat helpers (old store data may use weekColumns / weeklyCells) ───────

/* eslint-disable @typescript-eslint/no-explicit-any */
function getCells(obj: any): number[] {
  return obj.cells ?? obj.weeklyCells ?? [];
}
function getColumns(data: any): BoardResourcesReportResponse['columns'] {
  return data.columns ?? data.weekColumns ?? [];
}
function getScope(data: any): string {
  return data.scope ?? 'week';
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Props {
  data: BoardResourcesReportResponse;
}

const SCOPE_FILE_LABELS: Record<string, string> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
};

const BoardResourcesExportButton: React.FC<Props> = ({ data }) => {
  const handleExport = useCallback(async () => {
    try {
    const columns = getColumns(data);
    const scope = getScope(data);
    const { rows } = data;

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Board Resources');

    const scopeLabel = SCOPE_FILE_LABELS[scope] ?? scope;
    addTitleRow(ws, `Board Resources Report (${scopeLabel})`, data.period.startDate, data.period.endDate);

    // Header row
    const headers = ['Board Name', 'Person', 'Total (h)', ...columns.map((col) => col.label)];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);

    // Column widths
    setColumnWidths(ws, [30, 22, 12, ...columns.map(() => 14)]);

    for (const row of rows) {
      // Board row (aggregated) — styled as group
      const boardRow = ws.addRow([
        row.boardName,
        '',
        round(row.totalScheduledHours),
        ...getCells(row).map((v) => round(v)),
      ]);
      boardRow.height = 24;
      boardRow.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.primaryText) } };
      boardRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(COLORS.groupBg) } };
        cell.border = {
          top: { style: 'thin', color: { argb: hexToArgb(COLORS.borderColor) } },
          bottom: { style: 'thin', color: { argb: hexToArgb(COLORS.borderColor) } },
          left: { style: 'thin', color: { argb: hexToArgb(COLORS.borderColor) } },
          right: { style: 'thin', color: { argb: hexToArgb(COLORS.borderColor) } },
        };
      });

      // Color data cells with intensity based on hours
      const boardCells = getCells(row);
      const maxHours = Math.max(...boardCells, 1);
      for (let i = 0; i < boardCells.length; i++) {
        const cell = boardRow.getCell(4 + i);
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if (boardCells[i] > 0) {
          const intensity = Math.min(boardCells[i] / maxHours, 1);
          const alpha = Math.round(0.15 + intensity * 0.35);
          const r = Math.round(0 + (0 - 0) * alpha + 255 * (1 - alpha));
          const g = Math.round(115 * alpha + 255 * (1 - alpha));
          const b = Math.round(234 * alpha + 255 * (1 - alpha));
          const bgHex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(bgHex) } };
        }
      }

      // Person sub-rows
      for (const person of row.personSubRows) {
        const personCells = getCells(person);
        const personRow = ws.addRow([
          '',
          person.userName,
          round(person.totalScheduledHours),
          ...personCells.map((v) => round(v)),
        ]);
        styleDataRow(personRow);
        personRow.getCell(2).font = { size: 9 };
        // Center data cells
        for (let i = 0; i < personCells.length; i++) {
          personRow.getCell(4 + i).alignment = { horizontal: 'center', vertical: 'middle' };
        }
      }
    }

    // Summary row
    const summaryRow = ws.addRow([
      'Total',
      '',
      round(data.summary.totalScheduledHours),
    ]);
    styleTotalRow(summaryRow);

    await downloadWorkbook(wb, `board_resources_${scopeLabel}_report_${data.period.startDate}_${data.period.endDate}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  }, [data]);

  return (
    <Button
      kind={Button.kinds.SECONDARY}
      size={Button.sizes.SMALL}
      onClick={handleExport}
    >
      Export Excel
    </Button>
  );
};

export default BoardResourcesExportButton;